import React from 'react';
import { useNavigate } from 'react-router-dom';
import { UserProfile } from '../../../types/types';
import { AppSyncUser } from '../../../services/auth/types/user';
import { getTrips } from '../../../services/trips/api/tripsApi';
import DashboardLayout from '../../layouts/DashboardLayout';
import { ProfileTripPreview, mapTripToProfilePreview } from '../../utils/mapTripToProfilePreview';
import './ProfileTripsPage.css';

interface ProfileTripsPageProps {
  user: UserProfile;
  appSyncUser: AppSyncUser | null;
}

const ProfileTripsPage: React.FC<ProfileTripsPageProps> = ({ user, appSyncUser }) => {
  const navigate = useNavigate();
  const [profileTrips, setProfileTrips] = React.useState<ProfileTripPreview[]>([]);
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const profileName = user?.name && !user.name.includes('@') ? user.name : '';
  const displayName = [appSyncUser?.firstName, appSyncUser?.lastName].filter(Boolean).join(' ') || profileName || 'Emma Wilson';

  const fetchProfileTrips = React.useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const fetchedTrips = await getTrips();
      setProfileTrips(fetchedTrips.map((trip, index) => mapTripToProfilePreview(trip, index)));
    } catch (err: any) {
      setError(err.message || 'Failed to load profile trips.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  React.useEffect(() => {
    fetchProfileTrips();
  }, [fetchProfileTrips]);

  const openTrip = (selectedTrip: ProfileTripPreview) => {
    navigate(`/trips/${selectedTrip.id}`, {
      state: { backTo: '/profile/trips', owned: true, trip: selectedTrip.sourceTrip },
    });
  };

  return (
    <DashboardLayout activeSection="settings" wide>
      <div className="v2-profile-trips-page">
        <button type="button" className="v2-profile-trips-page__back" onClick={() => navigate('/profile')}>
          ← Back to profile
        </button>

        <header className="v2-profile-trips-page__header">
          <p>{displayName}</p>
          <h1>All trips</h1>
          <span>{isLoading ? 'Loading trips...' : `${profileTrips.length} trips`}</span>
        </header>

        {isLoading && <ProfileTripsPageState message="Loading trips..." />}
        {!isLoading && error && (
          <ProfileTripsPageState message={error} actionLabel="Retry" onAction={fetchProfileTrips} />
        )}
        {!isLoading && !error && profileTrips.length === 0 && <ProfileTripsPageState message="No trips yet." />}
        {!isLoading && !error && profileTrips.length > 0 && (
          <section className="v2-profile-trips-page__grid" aria-label="All profile trips">
            {profileTrips.map((trip) => (
              <button
                type="button"
                key={trip.id}
                className="v2-profile-trips-page__card"
                onClick={() => openTrip(trip)}
              >
                <img src={trip.imageUrl} alt="" />
                <div>
                  <h2>{trip.title}</h2>
                  <p>{trip.meta}</p>
                  <small>{trip.description}</small>
                </div>
              </button>
            ))}
          </section>
        )}
      </div>
    </DashboardLayout>
  );
};

interface ProfileTripsPageStateProps {
  message: string;
  actionLabel?: string;
  onAction?: () => void;
}

const ProfileTripsPageState: React.FC<ProfileTripsPageStateProps> = ({ message, actionLabel, onAction }) => (
  <div className="v2-profile-trips-page__state">
    <p>{message}</p>
    {actionLabel && onAction && (
      <button type="button" onClick={onAction}>
        {actionLabel}
      </button>
    )}
  </div>
);

export default ProfileTripsPage;
